import { useEffect, useState } from "react";
import { useAuth, http, formatApiErrorDetail } from "../lib/auth";import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Save, Plus, Trash2, ExternalLink, Check, Upload, Loader2 } from "lucide-react";

const TABS = [
  { id: "hero",     label: "Hero" },
  { id: "atelier",  label: "Atelier" },
  { id: "services", label: "Services" },
  { id: "gallery",  label: "Gallery" },
  { id: "stylists", label: "Stylists" },
  { id: "visit",    label: "Visit & Booking" },
  { id: "marquee",  label: "Marquee" },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("hero");
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [savedFlag, setSavedFlag] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (loading || !user) return;
    (async () => {
      const calls = [
        ["settings", () => http.get("/public/settings")],
        ["services", () => http.get("/admin/services")],
        ["gallery",  () => http.get("/admin/gallery")],
      ];
      const results = await Promise.allSettled(calls.map(([, fn]) => fn()));
      const failures = [];
      results.forEach((r, i) => {
        const [label] = calls[i];
        if (r.status === "fulfilled") {
          if (label === "settings") setSettings(r.value.data);
          if (label === "services") setServices(r.value.data);
          if (label === "gallery")  setGallery(r.value.data);
        } else {
          const e = r.reason;
          const detail = formatApiErrorDetail(e.response?.data?.detail);
          const status = e.response?.status ? `(HTTP ${e.response.status})` : "(network)";
          failures.push(`${label} ${status}${detail ? `: ${detail}` : ""}`);
        }
      });
      if (failures.length) {
        setError(`Couldn't load: ${failures.join(" · ")}. Pull-to-refresh or sign out & in.`);
      }
    })();
  }, [user, loading]);

  if (loading || user === null) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <div className="fs-overline" style={{ color: "var(--fs-caramel)" }}>Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const flash = (msg) => {
    setSavedFlag(msg);
    setTimeout(() => setSavedFlag(""), 2200);
  };

  const saveSettings = async () => {
    try {
      const { data } = await http.put("/admin/settings", settings);
      setSettings(data);
      flash("Settings saved");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div
      data-testid="admin-dashboard"
      style={{ minHeight: "100vh", background: "var(--fs-bg)", color: "var(--fs-ink)" }}
    >
      {/* Top bar */}
      <header
        style={{
          position: "sticky", top: 0, zIndex: 30,
          background: "rgba(248,242,231,0.92)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid var(--fs-line)",
        }}
      >
        <div className="fs-container flex items-center justify-between" style={{ height: "68px" }}>
          <div className="flex items-center gap-6">
            <a href="/" className="fs-overline" style={{ color: "var(--fs-muted)" }} data-testid="admin-back-site">← Site</a>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", fontWeight: 300 }}>
              Friends Salon&nbsp;<span className="fs-ital">24</span>
              <span className="fs-overline" style={{ marginLeft: "1rem", color: "var(--fs-muted)" }}>Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="fs-overline" style={{ color: "var(--fs-muted)" }} data-testid="admin-user-email">{user.email}</span>
            <button onClick={onLogout} className="fs-btn-ghost" style={{ padding: "0.6rem 1.1rem", fontSize: "0.62rem" }} data-testid="admin-logout">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="fs-container" style={{ paddingTop: "2.5rem", paddingBottom: "5rem" }}>
        {/* Tabs */}
        <nav className="flex flex-wrap gap-2" style={{ marginBottom: "2rem" }} data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="fs-overline"
              data-testid={`admin-tab-${t.id}`}
              style={{
                padding: "0.7rem 1.1rem",
                background: tab === t.id ? "var(--fs-caramel)" : "var(--fs-surface)",
                color: tab === t.id ? "var(--fs-surface)" : "var(--fs-ink-soft)",
                border: "1px solid",
                borderColor: tab === t.id ? "var(--fs-caramel)" : "var(--fs-line-strong)",
                cursor: "pointer",
                transition: "all 250ms ease",
                fontWeight: 600,
              }}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {error && (
          <div
            data-testid="admin-error"
            style={{ background: "rgba(224,123,123,0.1)", border: "1px solid #E07B7B", padding: "0.9rem 1.1rem", marginBottom: "1.5rem", color: "#E07B7B", fontSize: "0.9rem" }}
          >
            {error}
          </div>
        )}
        {savedFlag && (
          <div
            data-testid="admin-flash"
            style={{
              background: "rgba(196,166,126,0.1)",
              border: "1px solid var(--fs-caramel)",
              padding: "0.85rem 1.1rem",
              marginBottom: "1.5rem",
              color: "var(--fs-caramel)",
              fontSize: "0.85rem",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
            }}
          >
            <Check size={15} /> {savedFlag}
          </div>
        )}

        {!settings ? (
          <p style={{ color: "var(--fs-muted)" }}>Loading content…</p>
        ) : (
          <>
            {tab === "hero" && (
              <HeroEditor settings={settings} setSettings={setSettings} save={saveSettings} />
            )}
            {tab === "atelier" && (
              <AtelierEditor settings={settings} setSettings={setSettings} save={saveSettings} />
            )}
            {tab === "services" && (
              <ServicesEditor services={services} setServices={setServices} setError={setError} flash={flash} />
            )}
            {tab === "gallery" && (
              <GalleryEditor gallery={gallery} setGallery={setGallery} setError={setError} flash={flash} />
            )}
            {tab === "stylists" && (
              <StylistsEditor setError={setError} flash={flash} />
            )}
            {tab === "visit" && (
              <VisitEditor settings={settings} setSettings={setSettings} save={saveSettings} />
            )}
            {tab === "marquee" && (
              <MarqueeEditor settings={settings} setSettings={setSettings} save={saveSettings} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* =================== Section Editors =================== */

function Field({ label, children }) {
  return (
    <div>
      <label className="fs-label">{label}</label>
      {children}
    </div>
  );
}
function CardBox({ children, title, action }) {
  return (
    <section
      style={{
        background: "var(--fs-surface)",
        border: "1px solid var(--fs-line)",
        padding: "2rem",
        marginBottom: "1.5rem",
      }}
    >
      {title && (
        <header className="flex items-center justify-between" style={{ marginBottom: "1.5rem" }}>
          <h3 className="fs-overline" style={{ color: "var(--fs-caramel)", fontSize: "0.78rem" }}>{title}</h3>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

function HeroEditor({ settings, setSettings, save }) {
  const set = (k, v) => setSettings({ ...settings, [k]: v });
  return (
    <CardBox
      title="Hero Section"
      action={<button onClick={save} className="fs-btn-primary" style={{ padding: "0.7rem 1.4rem", fontSize: "0.66rem" }} data-testid="hero-save"><Save size={13} /> Save</button>}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Overline">
          <input className="fs-input" value={settings.hero_overline} onChange={(e) => set("hero_overline", e.target.value)} data-testid="hero-overline-input" />
        </Field>
        <Field label="Hero image URL">
          <input className="fs-input" value={settings.hero_image_url} onChange={(e) => set("hero_image_url", e.target.value)} data-testid="hero-image-input" />
        </Field>
        <Field label="Title – line 1">
          <input className="fs-input" value={settings.hero_title_line1} onChange={(e) => set("hero_title_line1", e.target.value)} data-testid="hero-title1-input" />
        </Field>
        <Field label="Title – italic accent">
          <input className="fs-input" value={settings.hero_title_italic} onChange={(e) => set("hero_title_italic", e.target.value)} data-testid="hero-italic-input" />
        </Field>
        <Field label="Title – line 2">
          <input className="fs-input" value={settings.hero_title_line2} onChange={(e) => set("hero_title_line2", e.target.value)} data-testid="hero-title2-input" />
        </Field>
        <Field label="Deposit note">
          <input className="fs-input" value={settings.deposit_note} onChange={(e) => set("deposit_note", e.target.value)} data-testid="hero-deposit-input" />
        </Field>
      </div>
      <div style={{ marginTop: "1.25rem" }}>
        <Field label="Subtitle paragraph">
          <textarea className="fs-input fs-textarea" value={settings.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} data-testid="hero-subtitle-input" />
        </Field>
      </div>
    </CardBox>
  );
}

function AtelierEditor({ settings, setSettings, save }) {
  const set = (k, v) => setSettings({ ...settings, [k]: v });
  return (
    <CardBox
      title="Atelier Story"
      action={<button onClick={save} className="fs-btn-primary" style={{ padding: "0.7rem 1.4rem", fontSize: "0.66rem" }} data-testid="atelier-save"><Save size={13} /> Save</button>}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Overline"><input className="fs-input" value={settings.atelier_overline} onChange={(e) => set("atelier_overline", e.target.value)} data-testid="atelier-overline-input" /></Field>
        <Field label="Atelier image URL"><input className="fs-input" value={settings.atelier_image_url} onChange={(e) => set("atelier_image_url", e.target.value)} data-testid="atelier-image-input" /></Field>
        <Field label="Title line 1"><input className="fs-input" value={settings.atelier_title_line1} onChange={(e) => set("atelier_title_line1", e.target.value)} data-testid="atelier-title1-input" /></Field>
        <Field label="Title italic accent"><input className="fs-input" value={settings.atelier_title_italic} onChange={(e) => set("atelier_title_italic", e.target.value)} data-testid="atelier-italic-input" /></Field>
      </div>
      <div className="grid md:grid-cols-2 gap-5" style={{ marginTop: "1.25rem" }}>
        <Field label="Paragraph 1"><textarea className="fs-input fs-textarea" value={settings.atelier_paragraph_1} onChange={(e) => set("atelier_paragraph_1", e.target.value)} data-testid="atelier-p1-input" /></Field>
        <Field label="Paragraph 2"><textarea className="fs-input fs-textarea" value={settings.atelier_paragraph_2} onChange={(e) => set("atelier_paragraph_2", e.target.value)} data-testid="atelier-p2-input" /></Field>
      </div>
      <div style={{ marginTop: "1.25rem" }}>
        <Field label="Pull quote"><input className="fs-input" value={settings.atelier_quote} onChange={(e) => set("atelier_quote", e.target.value)} data-testid="atelier-quote-input" /></Field>
      </div>
    </CardBox>
  );
}

function ServicesEditor({ services, setServices, setError, flash }) {
  const [draft, setDraft] = useState({ order: services.length, number: "", name: "", price_from: "", description: "" });
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    const { data } = await http.get("/admin/services");
    setServices(data);
  };

  const update = async (svc) => {
    setBusyId(svc.id);
    try {
      await http.put(`/admin/services/${svc.id}`, {
        order: svc.order, number: svc.number, name: svc.name, price_from: svc.price_from, description: svc.description ?? "",
      });
      flash("Service updated");
      await reload();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusyId(null); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await http.delete(`/admin/services/${id}`); flash("Service deleted"); await reload(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const create = async () => {
    if (!draft.name || !draft.number) { setError("Number and name are required"); return; }
    try {
      await http.post("/admin/services", draft);
      flash("Service added");
      setDraft({ order: services.length + 1, number: "", name: "", price_from: "", description: "" });
      await reload();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <>
      <CardBox title="Services">
        <div className="space-y-3">
          {services.map((s, idx) => (
            <div
              key={s.id}
              data-testid={`svc-item-${s.number}`}
              style={{
                border: "1px solid var(--fs-line)",
                padding: "1rem",
                background: "var(--fs-bg)",
              }}
            >
              <div className="grid md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-1"><Field label="Order"><input type="number" className="fs-input" value={s.order} onChange={(e) => { const v = [...services]; v[idx] = { ...s, order: Number(e.target.value) }; setServices(v); }} /></Field></div>
                <div className="md:col-span-1"><Field label="No."><input className="fs-input" value={s.number} onChange={(e) => { const v = [...services]; v[idx] = { ...s, number: e.target.value }; setServices(v); }} /></Field></div>
                <div className="md:col-span-3"><Field label="Name"><input className="fs-input" value={s.name} onChange={(e) => { const v = [...services]; v[idx] = { ...s, name: e.target.value }; setServices(v); }} /></Field></div>
                <div className="md:col-span-2"><Field label="From price"><input className="fs-input" value={s.price_from} onChange={(e) => { const v = [...services]; v[idx] = { ...s, price_from: e.target.value }; setServices(v); }} /></Field></div>
                <div className="md:col-span-3"><Field label="Description"><input className="fs-input" value={s.description || ""} onChange={(e) => { const v = [...services]; v[idx] = { ...s, description: e.target.value }; setServices(v); }} /></Field></div>
                <div className="md:col-span-2 flex gap-2">
                  <button onClick={() => update(s)} disabled={busyId === s.id} className="fs-btn-primary" style={{ padding: "0.7rem 1rem", fontSize: "0.62rem", flex: 1, justifyContent: "center" }} data-testid={`svc-save-${s.number}`}>
                    <Save size={12} /> Save
                  </button>
                  <button onClick={() => remove(s.id)} className="fs-btn-ghost" style={{ padding: "0.7rem 0.9rem", fontSize: "0.62rem" }} data-testid={`svc-delete-${s.number}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardBox>

      <CardBox title="Add a new service">
        <div className="grid md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-1"><Field label="Order"><input type="number" className="fs-input" value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} data-testid="new-svc-order" /></Field></div>
          <div className="md:col-span-1"><Field label="No."><input className="fs-input" value={draft.number} onChange={(e) => setDraft({ ...draft, number: e.target.value })} data-testid="new-svc-number" /></Field></div>
          <div className="md:col-span-3"><Field label="Name"><input className="fs-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="new-svc-name" /></Field></div>
          <div className="md:col-span-2"><Field label="From price"><input className="fs-input" value={draft.price_from} onChange={(e) => setDraft({ ...draft, price_from: e.target.value })} data-testid="new-svc-price" /></Field></div>
          <div className="md:col-span-3"><Field label="Description"><input className="fs-input" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="new-svc-desc" /></Field></div>
          <div className="md:col-span-2">
            <button onClick={create} className="fs-btn-primary" style={{ padding: "0.85rem 1rem", fontSize: "0.66rem", width: "100%", justifyContent: "center" }} data-testid="new-svc-add">
              <Plus size={13} /> Add
            </button>
          </div>
        </div>
      </CardBox>
    </>
  );
}

function GalleryEditor({ gallery, setGallery, setError, flash }) {
  const [draft, setDraft] = useState({ order: gallery.length, image_url: "", alt: "", span: "default" });
  const [uploadState, setUploadState] = useState({ busy: false, done: 0, total: 0, failed: [] });
  const [hover, setHover] = useState(false);

  const reload = async () => {
    const { data } = await http.get("/admin/gallery");
    setGallery(data);
  };
  const update = async (g) => {
    try {
      await http.put(`/admin/gallery/${g.id}`, { order: g.order, image_url: g.image_url, alt: g.alt ?? "", span: g.span ?? "default" });
      flash("Photo updated"); await reload();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await http.delete(`/admin/gallery/${id}`); flash("Photo deleted"); await reload(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const create = async () => {
    if (!draft.image_url) { setError("Image URL is required"); return; }
    try { await http.post("/admin/gallery", draft); flash("Photo added"); setDraft({ order: gallery.length + 1, image_url: "", alt: "", span: "default" }); await reload(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  // ---- Multi-file upload (drop or click) ----
  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /^image\/(jpeg|jpg|png|webp)$/i.test(f.type));
    if (files.length === 0) {
      setError("No supported image files found (JPG, PNG, or WEBP only).");
      return;
    }
    // Practical safety cap so we don't fire hundreds of requests at once
    const MAX_AT_ONCE = 50;
    const batch = files.slice(0, MAX_AT_ONCE);
    if (files.length > MAX_AT_ONCE) {
      setError(`Uploading the first ${MAX_AT_ONCE} of ${files.length} images. Repeat the drop for the rest.`);
    }

    setUploadState({ busy: true, done: 0, total: batch.length, failed: [] });
    const failed = [];
    let done = 0;

    // Upload sequentially to avoid slamming the API on shared hosting
    for (const file of batch) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await http.post("/admin/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) {
        failed.push(`${file.name} — ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`);
      }
      done += 1;
      setUploadState((s) => ({ ...s, done, failed }));
    }

    setUploadState({ busy: false, done, total: batch.length, failed });
    if (failed.length === 0) {
      flash(`${done} photo${done !== 1 ? "s" : ""} uploaded`);
    } else {
      setError(`Uploaded ${done - failed.length} · Failed ${failed.length}: ${failed.slice(0, 2).join("; ")}${failed.length > 2 ? "…" : ""}`);
    }
    await reload();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    if (uploadState.busy) return;
    uploadFiles(e.dataTransfer.files);
  };

  return (
    <>
      <CardBox title="Gallery">
        {/* Multi-file drag-and-drop zone */}
        <label
          onDragOver={(e) => { e.preventDefault(); setHover(true); }}
          onDragLeave={() => setHover(false)}
          onDrop={onDrop}
          data-testid="gallery-dropzone"
          style={{
            display: "block",
            marginBottom: "1.25rem",
            padding: "1.5rem 1.25rem",
            border: `1.5px dashed ${hover ? "var(--fs-caramel)" : "var(--fs-line-strong)"}`,
            background: hover ? "rgba(184,137,60,0.06)" : "var(--fs-bg)",
            cursor: uploadState.busy ? "wait" : "pointer",
            transition: "all 200ms ease",
            textAlign: "center",
          }}
        >
          <div className="fs-overline" style={{ color: "var(--fs-caramel)", marginBottom: "0.4rem" }}>
            {uploadState.busy
              ? `UPLOADING · ${uploadState.done}/${uploadState.total}`
              : "DROP UP TO 50 PHOTOS HERE OR CLICK TO CHOOSE"}
          </div>
          <div style={{ color: "var(--fs-muted)", fontSize: "0.82rem", lineHeight: 1.5 }}>
            JPG · PNG · WEBP, up to 8 MB each. New photos appear at the end of the gallery — reorder with the Order field.
          </div>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            disabled={uploadState.busy}
            onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
            data-testid="gallery-upload-input"
            style={{ display: "none" }}
          />
        </label>

        <div className="grid md:grid-cols-2 gap-4">
          {gallery.map((g, idx) => (
            <div key={g.id} data-testid={`gallery-item-row-${idx}`} style={{ border: "1px solid var(--fs-line)", padding: "1rem", background: "var(--fs-bg)" }}>
              <div className="flex gap-4">
                <div style={{ width: "100px", height: "120px", flexShrink: 0, background: "var(--fs-surface-alt)", border: "1px solid var(--fs-line)" }}>
                  {g.image_url && <img src={g.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
                <div style={{ flex: 1 }} className="space-y-3">
                  <Field label="Image URL">
                    <input className="fs-input" value={g.image_url} onChange={(e) => { const v = [...gallery]; v[idx] = { ...g, image_url: e.target.value }; setGallery(v); }} />
                  </Field>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Order"><input type="number" className="fs-input" value={g.order} onChange={(e) => { const v = [...gallery]; v[idx] = { ...g, order: Number(e.target.value) }; setGallery(v); }} /></Field>
                    <Field label="Alt"><input className="fs-input" value={g.alt || ""} onChange={(e) => { const v = [...gallery]; v[idx] = { ...g, alt: e.target.value }; setGallery(v); }} /></Field>
                    <Field label="Span">
                      <select className="fs-input" value={g.span || "default"} onChange={(e) => { const v = [...gallery]; v[idx] = { ...g, span: e.target.value }; setGallery(v); }}>
                        <option value="default">Default</option>
                        <option value="wide">Wide</option>
                        <option value="tall">Tall</option>
                      </select>
                    </Field>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => update(g)} className="fs-btn-primary" style={{ padding: "0.6rem 1rem", fontSize: "0.62rem" }} data-testid={`gallery-save-${idx}`}><Save size={12} /> Save</button>
                    <button onClick={() => remove(g.id)} className="fs-btn-ghost" style={{ padding: "0.6rem 0.9rem", fontSize: "0.62rem" }} data-testid={`gallery-delete-${idx}`}><Trash2 size={12} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardBox>

      <CardBox title="Add a new photo">
        <div className="grid md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-1"><Field label="Order"><input type="number" className="fs-input" value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} data-testid="new-gallery-order" /></Field></div>
          <div className="md:col-span-5"><Field label="Image URL"><input className="fs-input" value={draft.image_url} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })} placeholder="/fs24/assets/photos/your.jpg or full https URL" data-testid="new-gallery-image-url" /></Field></div>
          <div className="md:col-span-3"><Field label="Alt text"><input className="fs-input" value={draft.alt} onChange={(e) => setDraft({ ...draft, alt: e.target.value })} data-testid="new-gallery-alt" /></Field></div>
          <div className="md:col-span-2">
            <Field label="Span">
              <select className="fs-input" value={draft.span} onChange={(e) => setDraft({ ...draft, span: e.target.value })} data-testid="new-gallery-span">
                <option value="default">Default</option>
                <option value="wide">Wide</option>
                <option value="tall">Tall</option>
              </select>
            </Field>
          </div>
          <div className="md:col-span-1">
            <button onClick={create} className="fs-btn-primary" style={{ padding: "0.85rem 1rem", fontSize: "0.66rem", width: "100%", justifyContent: "center" }} data-testid="new-gallery-add">
              <Plus size={13} />
            </button>
          </div>
        </div>
      </CardBox>
    </>
  );
}

function VisitEditor({ settings, setSettings, save }) {
  const set = (k, v) => setSettings({ ...settings, [k]: v });
  return (
    <CardBox
      title="Visit & Booking"
      action={<button onClick={save} className="fs-btn-primary" style={{ padding: "0.7rem 1.4rem", fontSize: "0.66rem" }} data-testid="visit-save"><Save size={13} /> Save</button>}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Business name"><input className="fs-input" value={settings.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="visit-name-input" /></Field>
        <Field label="Address line"><input className="fs-input" value={settings.address_line} onChange={(e) => set("address_line", e.target.value)} data-testid="visit-address-input" /></Field>
        <Field label="Hours line"><input className="fs-input" value={settings.hours_line} onChange={(e) => set("hours_line", e.target.value)} data-testid="visit-hours-input" /></Field>
        <Field label="Phone (display)"><input className="fs-input" value={settings.phone_display} onChange={(e) => set("phone_display", e.target.value)} data-testid="visit-phone-display-input" /></Field>
        <Field label="Phone (digits only — for sms: and tel: links)"><input className="fs-input" value={settings.phone_digits} onChange={(e) => set("phone_digits", e.target.value.replace(/\D/g, ""))} data-testid="visit-phone-digits-input" /></Field>
        <Field label="Visit image URL"><input className="fs-input" value={settings.visit_image_url} onChange={(e) => set("visit_image_url", e.target.value)} data-testid="visit-image-input" /></Field>
      </div>

      <div className="grid md:grid-cols-2 gap-5" style={{ marginTop: "1.25rem" }}>
        <Field label="Square Appointments URL">
          <div className="flex gap-2 items-stretch">
            <input className="fs-input" value={settings.square_booking_url} onChange={(e) => set("square_booking_url", e.target.value)} placeholder="https://book.squareup.com/..." data-testid="visit-square-input" />
            {settings.square_booking_url && (
              <a href={settings.square_booking_url} target="_blank" rel="noopener noreferrer" className="fs-btn-ghost" style={{ padding: "0 0.9rem", fontSize: "0.62rem" }} data-testid="visit-square-test">
                <ExternalLink size={13} />
              </a>
            )}
          </div>
        </Field>
        <Field label="Deposit note"><input className="fs-input" value={settings.deposit_note} onChange={(e) => set("deposit_note", e.target.value)} data-testid="visit-deposit-input" /></Field>
      </div>
    </CardBox>
  );
}

function MarqueeEditor({ settings, setSettings, save }) {
  // Augment with stable IDs so React row keys survive add/remove (avoid input focus jumps).
  // The persisted shape on the server is still `string[]`; IDs are local-only.
  const [rows, setRows] = useState(() =>
    (settings.marquee_items || []).map((text, i) => ({ id: `m-${Date.now()}-${i}`, text }))
  );

  // When parent settings change (e.g. after save round-trip), resync if the texts differ.
  useEffect(() => {
    const incoming = settings.marquee_items || [];
    const current = rows.map((r) => r.text);
    if (JSON.stringify(incoming) !== JSON.stringify(current)) {
      setRows(incoming.map((text, i) => ({ id: `m-${Date.now()}-${i}`, text })));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only resync when the underlying texts change
  }, [settings.marquee_items]);

  const updateRows = (next) => {
    setRows(next);
    setSettings({ ...settings, marquee_items: next.map((r) => r.text) });
  };

  return (
    <CardBox
      title="Marquee Phrases"
      action={
        <div className="flex gap-2">
          <button onClick={() => updateRows([...rows, { id: `m-${Date.now()}-${rows.length}`, text: "New phrase" }])} className="fs-btn-ghost" style={{ padding: "0.7rem 1rem", fontSize: "0.62rem" }} data-testid="marquee-add">
            <Plus size={12} /> Add phrase
          </button>
          <button onClick={save} className="fs-btn-primary" style={{ padding: "0.7rem 1.4rem", fontSize: "0.66rem" }} data-testid="marquee-save">
            <Save size={13} /> Save
          </button>
        </div>
      }
    >
      <div className="space-y-2">
        {rows.map((row, idx) => (
          <div key={row.id} className="flex gap-2 items-center" data-testid={`marquee-row-${idx}`}>
            <input
              className="fs-input"
              value={row.text}
              onChange={(e) => updateRows(rows.map((r) => (r.id === row.id ? { ...r, text: e.target.value } : r)))}
            />
            <button
              className="fs-btn-ghost"
              style={{ padding: "0.7rem 0.9rem", fontSize: "0.62rem" }}
              onClick={() => updateRows(rows.filter((r) => r.id !== row.id))}
              data-testid={`marquee-delete-${idx}`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
    </CardBox>
  );
}


function StylistsEditor({ setError, flash }) {
  const [list, setList] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    try {
      const { data } = await http.get("/admin/stylists");
      setList(data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async (s) => {
    setBusyId(s.team_member_id);
    try {
      await http.put(`/admin/stylists/${s.team_member_id}`, {
        photo_url: s.photo_url || "",
        tagline: s.tagline || "",
        bio: s.bio || "",
        order: Number(s.order) || 0,
      });
      flash("Stylist saved");
      await reload();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusyId(null); }
  };

  if (!list) {
    return <CardBox title="Stylists"><p style={{ color: "var(--fs-muted)" }}>Loading from Square…</p></CardBox>;
  }
  if (list.length === 0) {
    return <CardBox title="Stylists"><p style={{ color: "var(--fs-muted)" }}>No bookable stylists found in Square. Add team members in your Square Dashboard.</p></CardBox>;
  }

  return (
    <CardBox title="Stylist Match · Photos & Taglines">
      <p style={{ color: "var(--fs-muted)", fontSize: "0.85rem", marginBottom: "1.25rem", lineHeight: 1.6 }}>
        Stylists are pulled from Square. Add a portrait photo URL and a bio so each stylist&apos;s card on the public &ldquo;The Team&rdquo; section feels personal.
        Specialty chips are derived automatically from the services they&apos;re assigned to in Square.
      </p>
      <div className="space-y-3">
        {list.map((s, idx) => (
          <div
            key={s.team_member_id}
            data-testid={`stylist-row-${s.team_member_id}`}
            style={{ border: "1px solid var(--fs-line)", padding: "1rem", background: "var(--fs-bg)" }}
          >
            <div className="flex gap-4 items-start">
              <div
                style={{
                  width: 84, height: 100, flexShrink: 0,
                  border: "1px solid var(--fs-caramel-line)",
                  background: s.photo_url
                    ? `center/cover no-repeat url("${s.photo_url}")`
                    : "var(--fs-surface-alt)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Cormorant Garamond', serif", color: "var(--fs-caramel)", fontSize: "1.6rem",
                }}
                aria-hidden
              >
                {!s.photo_url && (s.full_name || "·").split(" ").slice(0, 2).map((p) => p[0]?.toUpperCase()).join("")}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="grid md:grid-cols-12 gap-3">
                  <div className="md:col-span-4">
                    <Field label="Stylist (from Square)">
                      <input className="fs-input" value={s.full_name} readOnly disabled />
                    </Field>
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Order">
                      <input
                        type="number"
                        className="fs-input"
                        value={s.order}
                        onChange={(e) => { const v = [...list]; v[idx] = { ...s, order: Number(e.target.value) }; setList(v); }}
                        data-testid={`stylist-order-${s.team_member_id}`}
                      />
                    </Field>
                  </div>
                  <div className="md:col-span-6">
                    <Field label="Photo (drop a JPG / PNG / WEBP — or paste a URL below)">
                      <PhotoDropZone
                        teamMemberId={s.team_member_id}
                        currentUrl={s.photo_url}
                        onUploaded={(url) => { const v = [...list]; v[idx] = { ...s, photo_url: url }; setList(v); flash("Photo uploaded"); }}
                        onError={(msg) => setError(msg)}
                      />
                      <input
                        className="fs-input"
                        style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}
                        value={s.photo_url}
                        placeholder="…or paste an https URL"
                        onChange={(e) => { const v = [...list]; v[idx] = { ...s, photo_url: e.target.value }; setList(v); }}
                        data-testid={`stylist-photo-${s.team_member_id}`}
                      />
                    </Field>
                  </div>
                  <div className="md:col-span-9" style={{ display: "none" }}></div>
                  <div className="md:col-span-3" style={{ display: "flex", alignItems: "flex-end" }}>
                    <button
                      onClick={() => save(s)}
                      disabled={busyId === s.team_member_id}
                      className="fs-btn-primary"
                      style={{ padding: "0.85rem 1rem", fontSize: "0.66rem", width: "100%", justifyContent: "center" }}
                      data-testid={`stylist-save-${s.team_member_id}`}
                    >
                      <Save size={12} /> Save
                    </button>
                  </div>
                  <div className="md:col-span-12">
                    <Field label="Bio (shown on the public 'Meet the handpicked' section)">
                      <textarea
                        className="fs-input"
                        rows={4}
                        value={s.bio || ""}
                        placeholder="Two or three sentences about this stylist's craft, signature work, and what clients love."
                        onChange={(e) => { const v = [...list]; v[idx] = { ...s, bio: e.target.value }; setList(v); }}
                        data-testid={`stylist-bio-${s.team_member_id}`}
                        style={{ resize: "vertical", minHeight: "92px", lineHeight: 1.5 }}
                      />
                    </Field>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </CardBox>
  );
}

function PhotoDropZone({ teamMemberId, currentUrl, onUploaded, onError }) {
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      onError(`Unsupported file type: ${file.type || "unknown"}. Use JPG, PNG, or WEBP.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`/admin/stylists/${teamMemberId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.photo_url);
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    upload(e.dataTransfer.files?.[0]);
  };

  return (
    <label
      htmlFor={`photo-input-${teamMemberId}`}
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={onDrop}
      data-testid={`photo-dropzone-${teamMemberId}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.85rem",
        padding: "0.85rem 1rem",
        border: `1.5px dashed ${hover ? "var(--fs-caramel)" : "var(--fs-line-strong)"}`,
        background: hover ? "rgba(184,137,60,0.06)" : "var(--fs-bg)",
        cursor: busy ? "wait" : "pointer",
        transition: "all 200ms ease",
        minHeight: 70,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 52, height: 52, flexShrink: 0,
          border: "1px solid var(--fs-caramel-line)",
          background: currentUrl
            ? `center/cover no-repeat url("${currentUrl}")`
            : "var(--fs-surface-alt)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--fs-caramel)",
        }}
      >
        {!currentUrl && <Upload size={18} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fs-overline" style={{ color: "var(--fs-ink)", fontSize: "0.66rem" }}>
          {busy ? "Uploading…" : currentUrl ? "Drop a new photo to replace" : "Drop a photo here, or click to choose"}
        </div>
        <div style={{ color: "var(--fs-muted)", fontSize: "0.78rem", marginTop: 2, lineHeight: 1.4 }}>
          JPG · PNG · WEBP, up to 8 MB. 4:5 portrait looks best.
        </div>
      </div>
      {busy && <Loader2 size={16} className="spin" style={{ color: "var(--fs-caramel)" }} />}
      <input
        id={`photo-input-${teamMemberId}`}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
        disabled={busy}
        data-testid={`photo-input-${teamMemberId}`}
        style={{ display: "none" }}
      />
    </label>
  );
}
